import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const formatPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return phone;
};

// Interceptor para agregar token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar token expirado
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginEndpoint = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Users
export const getUsers = async () => {
  const response = await axios.get(`${API}/users`);
  return response.data;
};

export const createUser = async (data) => {
  const response = await axios.post(`${API}/auth/register`, data);
  return response.data;
};

export const updateUser = async (userId, data) => {
  const response = await axios.put(`${API}/users/${userId}`, data);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await axios.delete(`${API}/users/${userId}`);
  return response.data;
};

// Products
export const getProducts = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/products?${params}`);
  return response.data;
};

export const getAllProducts = async (filters = {}) => {
  const params = new URLSearchParams({ ...filters, per_page: 0 }).toString();
  const response = await axios.get(`${API}/products?${params}`);
  return Array.isArray(response.data) ? response.data : (response.data.products || []);
};

export const getAllProductsBySales = async (branchId = '') => {
  const params = new URLSearchParams({ per_page: 0, sort_by: 'sales' });
  if (branchId) params.set('branch_id', branchId);
  const response = await axios.get(`${API}/products?${params}`);
  return Array.isArray(response.data) ? response.data : (response.data.products || []);
};

export const getTopSoldProducts = async (limit = 5, branchId = '', kind = 'products') => {
  const params = new URLSearchParams({ limit, kind });
  if (branchId) params.set('branch_id', branchId);
  const response = await axios.get(`${API}/products/top-sold?${params}`);
  return response.data;
};

export const getProduct = async (id) => {
  const response = await axios.get(`${API}/products/${id}`);
  return response.data;
};

export const createProduct = async (data) => {
  const response = await axios.post(`${API}/products`, data);
  return response.data;
};

export const updateProduct = async (id, data, userId = null) => {
  const params = userId ? `?user_id=${userId}` : '';
  const response = await axios.put(`${API}/products/${id}${params}`, data);
  return response.data;
};

export const deleteProduct = async (id) => {
  const response = await axios.delete(`${API}/products/${id}`);
  return response.data;
};

export const uploadProductImage = async (productId, file) => {
  const formData = new FormData();
  formData.append('image', file);
  const response = await axios.post(`${API}/products/${productId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteProductImage = async (productId) => {
  const response = await axios.delete(`${API}/products/${productId}/image`);
  return response.data;
};

export const migrateProductCodes = async () => {
  const response = await axios.post(`${API}/products/migrate-codes`);
  return response.data;
};

export const bulkPriceAdjust = async (pct) => {
  const response = await axios.post(`${API}/products/bulk-price-adjust`, { pct });
  return response.data;
};

export const getLowStockAlerts = async () => {
  const response = await axios.get(`${API}/products/alerts/low-stock`);
  return response.data;
};

// Customers
export const getCustomers = async () => {
  const response = await axios.get(`${API}/customers`);
  return response.data;
};

export const getCustomer = async (id) => {
  const response = await axios.get(`${API}/customers/${id}`);
  return response.data;
};

export const createCustomer = async (data) => {
  const response = await axios.post(`${API}/customers`, data);
  return response.data;
};

export const updateCustomer = async (id, data) => {
  const response = await axios.put(`${API}/customers/${id}`, data);
  return response.data;
};

export const updateCustomerProfile = async (customerId, data) => {
  const response = await axios.post(`${API}/customers/${customerId}/profile-update`, data);
  return response.data;
};

export const checkCashRegister = async () => {
  const response = await axios.get(`${API}/cash-register/check`);
  return response.data;
};

// ── Cash Register ──
export const openCashRegister = async (data) => {
  const response = await axios.post(`${API}/cash-register/open`, data);
  return response.data;
};

export const closeCashRegister = async (data) => {
  const response = await axios.post(`${API}/cash-register/close`, data);
  return response.data;
};

export const getTodayCashRegister = async (branchId = '') => {
  const params = branchId ? `?branch_id=${branchId}` : '';
  const response = await axios.get(`${API}/cash-register/today${params}`);
  return response.data;
};

export const getCashRegisterBreakdown = async (registerId) => {
  const response = await axios.get(`${API}/cash-register/${registerId}/breakdown`);
  return response.data;
};

export const sendCashRegisterReport = async (registerId, email) => {
  const response = await axios.post(`${API}/cash-register/${registerId}/send-report`, { email });
  return response.data;
};

export const addCashRegisterExpense = async (registerId, data) => {
  const response = await axios.post(`${API}/cash-register/${registerId}/expenses`, data);
  return response.data;
};

export const getCashRegisterHistory = async (filters = {}, page = 1, perPage = 20) => {
  const p = { page, per_page: perPage };
  if (filters.branch_id) p.branch_id = filters.branch_id;
  if (filters.date_from)  p.date_from  = filters.date_from;
  if (filters.date_to)    p.date_to    = filters.date_to;
  if (filters.user_q)     p.user_q     = filters.user_q;
  const response = await axios.get(`${API}/cash-register/history?${new URLSearchParams(p)}`);
  return response.data;
};

export const changeMyPassword = async (data) => {
  const response = await axios.put(`${API}/users/me/password`, data);
  return response.data;
};

export const changeUserPassword = async (userId, newPassword) => {
  const response = await axios.put(`${API}/users/${userId}/password`, { new_password: newPassword });
  return response.data;
};

export const updateMyPreferences = async (data) => {
  const response = await axios.put(`${API}/users/me/preferences`, data);
  return response.data;
};

export const getNotificationConfig = async () => {
  const response = await axios.get(`${API}/notification-config`);
  return response.data;
};

export const updateNotificationConfig = async (data) => {
  const response = await axios.put(`${API}/notification-config`, data);
  return response.data;
};

export const sendInvoiceEmail = async (invoiceId, email) => {
  const response = await axios.post(`${API}/invoices/${invoiceId}/send-email`, { email });
  return response.data;
};

export const deleteCustomer = async (id) => {
  const response = await axios.delete(`${API}/customers/${id}`);
  return response.data;
};

// Invoices
export const getInvoices = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/invoices?${params}`);
  return response.data;
};

export const getInvoicesPaged = async (filters = {}, page = 1, perPage = 25) => {
  const params = new URLSearchParams({ ...filters, page, per_page: perPage }).toString();
  const response = await axios.get(`${API}/invoices?${params}`);
  return response.data; // { invoices, total, page, per_page, total_pages }
};

export const getInvoice = async (id) => {
  const response = await axios.get(`${API}/invoices/${id}`);
  return response.data;
};

export const createInvoice = async (data, userId) => {
  const response = await axios.post(`${API}/invoices?user_id=${userId}`, data);
  return response.data;
};

export const downloadInvoicePDF = async (id) => {
  const response = await axios.get(`${API}/invoices/${id}/pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

// Quotations
export const getQuotations = async () => {
  const response = await axios.get(`${API}/quotations`);
  return response.data;
};

export const createQuotation = async (data, userId) => {
  const response = await axios.post(`${API}/quotations?user_id=${userId}`, data);
  return response.data;
};

export const updateQuotationStatus = async (id, status) => {
  const response = await axios.put(`${API}/quotations/${id}/status?status=${status}`);
  return response.data;
};

// Credit Notes
export const getCreditNotes = async () => {
  const response = await axios.get(`${API}/credit-notes`);
  return response.data;
};

export const createCreditNote = async (data, userId) => {
  const response = await axios.post(`${API}/credit-notes?user_id=${userId}`, data);
  return response.data;
};

// Reports
export const getSalesReport = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/reports/sales?${params}`);
  return response.data;
};

export const getTopProducts = async (limit = 10) => {
  const response = await axios.get(`${API}/reports/top-products?limit=${limit}`);
  return response.data;
};

export const getBottomProducts = async (limit = 10) => {
  const response = await axios.get(`${API}/reports/bottom-products?limit=${limit}`);
  return response.data;
};

// Dashboard
export const getDashboardStats = async (branchId = '') => {
  const params = branchId ? `?branch_id=${branchId}` : '';
  const response = await axios.get(`${API}/dashboard/stats${params}`);
  return response.data;
};

// Categories
export const getCategories = async () => {
  const response = await axios.get(`${API}/categories`);
  return response.data;
};
export const createCategory = async (name) => {
  const response = await axios.post(`${API}/categories`, { name });
  return response.data;
};
export const updateCategory = async (id, name) => {
  const response = await axios.put(`${API}/categories/${id}`, { name });
  return response.data;
};
export const deleteCategory = async (id) => {
  const response = await axios.delete(`${API}/categories/${id}`);
  return response.data;
};

// NCF Config
export const getNCFConfig = async () => {
  const response = await axios.get(`${API}/ncf-config`);
  return response.data;
};

export const updateNCFConfig = async (tipo, data) => {
  const response = await axios.put(`${API}/ncf-config/${tipo}`, data);
  return response.data;
};

// Company Config
export const getCompanyConfig = async () => {
  const response = await axios.get(`${API}/company-config`);
  return response.data;
};

export const updateCompanyConfig = async (data) => {
  const response = await axios.put(`${API}/company-config`, data);
  return response.data;
};

export const uploadCompanyLogo = async (logo_b64, show_logo = true) => {
  const response = await axios.post(`${API}/company-config/logo`, { logo_b64, show_logo });
  return response.data;
};

export const deleteCompanyLogo = async () => {
  const response = await axios.delete(`${API}/company-config/logo`);
  return response.data;
};

// Print Config
export const getPrintConfig = async () => {
  const response = await axios.get(`${API}/print-config`);
  return response.data;
};

export const updatePrintConfig = async (data) => {
  const response = await axios.put(`${API}/print-config`, data);
  return response.data;
};

export const downloadTailoringReceipt = async (orderId) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tailoring-orders/${orderId}/receipt`, {
    method: 'GET',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
  });

  if (!response.ok) {
    const errJson = await response.json().catch(() => ({ error: `Error ${response.status}` }));
    throw new Error(errJson.error || `Error ${response.status}`);
  }

  return await response.blob();
};

export const sendTailoringReceipt = async (orderId, email) => {
  const response = await axios.post(`${API}/tailoring-orders/${orderId}/send-receipt`, { email });
  return response.data;
};

// Branches (Sucursales)
export const getBranches = async () => {
  const response = await axios.get(`${API}/branches`);
  return response.data;
};

export const getBranch = async (branchId) => {
  const response = await axios.get(`${API}/branches/${branchId}`);
  return response.data;
};

export const createBranch = async (data) => {
  const response = await axios.post(`${API}/branches`, data);
  return response.data;
};

export const updateBranch = async (branchId, data) => {
  const response = await axios.put(`${API}/branches/${branchId}`, data);
  return response.data;
};

export const deleteBranch = async (branchId) => {
  const response = await axios.delete(`${API}/branches/${branchId}`);
  return response.data;
};

// Branch Inventory
export const getBranchInventory = async (branchId, productId) => {
  const params = new URLSearchParams();
  if (branchId) params.append('branch_id', branchId);
  if (productId) params.append('product_id', productId);
  const response = await axios.get(`${API}/branch-inventory?${params.toString()}`);
  return response.data;
};

export const createBranchInventory = async (data) => {
  const response = await axios.post(`${API}/branch-inventory`, data);
  return response.data;
};

export const updateBranchInventory = async (inventoryId, data) => {
  const response = await axios.put(`${API}/branch-inventory/${inventoryId}`, data);
  return response.data;
};

// Inventory Transfers
export const getInventoryTransfers = async () => {
  const response = await axios.get(`${API}/inventory-transfers`);
  return response.data;
};

export const createInventoryTransfer = async (data) => {
  const response = await axios.post(`${API}/inventory-transfers`, data);
  return response.data;
};

// Tailoring Orders (Pedidos de Confección)
export const getTailoringOrders = async (filters = {}, page = 1, perPage = 10) => {
  const params = new URLSearchParams({ ...filters, page, per_page: perPage }).toString();
  const response = await axios.get(`${API}/tailoring-orders?${params}`);
  return response.data; // { orders, total, page, per_page, total_pages }
};

export const getTailoringOrder = async (id) => {
  const response = await axios.get(`${API}/tailoring-orders/${id}`);
  return response.data;
};

export const createTailoringOrder = async (data) => {
  const response = await axios.post(`${API}/tailoring-orders`, data);
  return response.data;
};

export const validateTailoringOrder = async (id) => {
  const response = await axios.post(`${API}/tailoring-orders/${id}/validate`);
  return response.data;
};

export const updateTailoringOrderStatus = async (id, payload) => {
  const body = typeof payload === "string" ? { status: payload } : payload;
  const res = await axios.post(`${API}/tailoring-orders/${id}/status`, body);
  return res.data;
};


export const addTailoringPayment = async (id, data) => {
  const response = await axios.post(`${API}/tailoring-orders/${id}/payments`, data);
  return response.data;
};

export const getTailoringPayments = async (id) => {
  const response = await axios.get(`${API}/tailoring-orders/${id}/payments`);
  return response.data;
};
export const updateTailoringOrder = async (id, data) => {
  const response = await axios.patch(`${API}/tailoring-orders/${id}`, data);
  return response.data;
};
export const cancelTailoringOrder = async (id, reason) => {
  const res = await axios.post(`${API}/tailoring-orders/${id}/cancel`, { reason });
  return res.data;
};

export const invoiceTailoringOrder = async (id, data) => {
  const response = await axios.post(`${API}/tailoring-orders/${id}/invoice`, data);
  return response.data;
};

export const getTailoringOrderInvoices = async (id) => {
  const res = await axios.get(`${API}/tailoring-orders/${id}/invoices`);
  return res.data;
};

// Service Orders (Órdenes de Servicio)
export const getServiceOrders = async (filters = {}, page = 1, perPage = 25) => {
  const params = new URLSearchParams({ ...filters, page, per_page: perPage }).toString();
  const response = await axios.get(`${API}/service-orders?${params}`);
  return response.data; // { items, total, page, per_page, total_pages }
};

export const createServiceOrder = async (data) => {
  const response = await axios.post(`${API}/service-orders`, data);
  return response.data;
};

export const getServiceOrder = async (id) => {
  const response = await axios.get(`${API}/service-orders/${id}`);
  return response.data;
};

export const updateServiceOrder = async (id, data) => {
  const response = await axios.patch(`${API}/service-orders/${id}`, data);
  return response.data;
};

export const updateServiceOrderStatus = async (id, status) => {
  const response = await axios.post(`${API}/service-orders/${id}/status`, { status });
  return response.data;
};

export const getRecentCustomers = async () => {
  const response = await axios.get(`${API}/customers/recent`);
  return response.data;
};

export const updateSubOrderStatus = async (orderId, subOrderId, status) => {
  const response = await axios.post(`${API}/service-orders/${orderId}/sub-orders/${subOrderId}/status`, { status });
  return response.data;
};

export const addServiceOrderNote = async (orderId, text) => {
  const response = await axios.post(`${API}/service-orders/${orderId}/notes`, { text });
  return response.data;
};

export const downloadServiceReceipt = async (orderId) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/service-orders/${orderId}/receipt`, {
    method: 'GET',
    headers: { 'Authorization': token ? `Bearer ${token}` : '' },
  });
  if (!response.ok) {
    const errJson = await response.json().catch(() => ({ error: `Error ${response.status}` }));
    throw new Error(errJson.error || `Error ${response.status}`);
  }
  return await response.blob();
};

export const sendServiceReceipt = async (orderId, email) => {
  const response = await axios.post(`${API}/service-orders/${orderId}/send-receipt`, { email });
  return response.data;
};